<template>
  <div
    class="size-guide"
    js-body-scroll-lock-ignore
  >
    <!-- Size Guide Table -->
    <div
      class="size-guide__table size-guide__table--size"
    >
      <div class="size-guide__header">
        <span
          v-if="displayTable(tables.sizeGuideTable)"
          class="text-desktop-small text-regular"
          v-text="showSwitcher
            ? $string('product.size_guide.product_measurements')
            : $string('product.size_guide.size_conversion')"
        />

        <div
          v-if="showSwitcher"
          class="size-guide__switcher"
        >
          <button
            class="text-desktop-small size-guide__switcher-action"
            :class="{
              [$class('active')]: !showInches
            }"
            type="button"
            @click="() => showInches = false"
            v-text="$string('product.size_guide.cm')"
          />

          <button
            class="text-desktop-small size-guide__switcher-action"
            :class="{
              [$class('active')]: showInches
            }"
            type="button"
            @click="() => showInches = true"
            v-text="$string('product.size_guide.inches')"
          />
        </div>
      </div>

      <keep-alive>
        <size-guide-table
          v-if="displayTable(tables.sizeGuideTable)"
          emits-click-events
          :table="tables.sizeGuideTable"
          :show-inches="showSwitcher ? showInches : null"
          :variant-column="variantColumn"
          @column-updated="handleColumnVariantUpdate"
        />
      </keep-alive>
    </div>

    <!-- Measurements table -->
    <div
      v-if="bodyMeasurementData && bodyMeasurementData.length"
      class="size-guide__table size-guide__table--measurement"
    >
      <div class="size-guide__header">
        <span
          class="text-desktop-small text-regular"
          v-text="$string('product.size_guide.garment_measurement')"
        />

        <div class="size-guide__switcher">
          <button
            class="text-desktop-small size-guide__switcher-action"
            :class="{
              [$class('active')]: !showInches
            }"
            type="button"
            @click="() => showInches = false"
            v-text="$string('product.size_guide.cm')"
          />

          <button
            class="text-desktop-small size-guide__switcher-action"
            :class="{
              [$class('active')]: showInches
            }"
            type="button"
            @click="() => showInches = true"
            v-text="$string('product.size_guide.inches')"
          />
        </div>
      </div>

      <ul
        class="size-guide__table-list"
      >
        <li
          v-for="(size, index) of bodyMeasurementData"
          :key="`size-list-item-${index}`"
          class="size-guide__table-list-item"
        >
          <span
            class="text-desktop-mini"
            v-text="size.name"
          />
          <span
            class="text-desktop-mini"
            v-text="showSizeByUnit(size.value)"
          />
        </li>
      </ul>
    </div>

    <template>
      <div class="size-guide__modal">
        <div class="size-guide__modal-body">
          <responsive-image
            alt="test"
            block-class="size-guide__modal"
            :image="{
              url: sizeGuideImage
            }"
            :padding="false"
            ratio="image"
          />
        </div>

        <div
          v-if="displaySizeGuideImageFooter"
          class="size-guide__modal-footer rte"
        >
          <ul class="size-guide__modal-footer-list">
            <li
              v-for="(point, index) of product.sizeGuide?.keyPoints"
              :key="`key-point-${index}`"
              class="text-desktop-small"
              v-text="point"
            />
            <li
              v-if="product.sizeGuide?.modelDescription"
              class="text-desktop-small"
              v-text="product.sizeGuide?.modelDescription"
            />
          </ul>
        </div>
      </div>
    </template>
  </div>
</template>

<script>

/**
 * Vue: Size guide (size-guide)
 * -----------------------------------------------------------------------------
 * Size guide displaying an image and table created from CSV.
 *
 * @param {Boolean, Object} product - Shopify product object.
 * @param {Boolean} showSizeGuide - Indicates if the size guide is displayed.
 * @param {Boolean, Object} variant - Shopify product variant object.
 */
import Papa from 'papaparse'
import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'

import ResponsiveImage from '~global/images/responsive-image'
import SizeGuideTable from './size-guide-table'

import { mapState } from 'vuex'

export default {
  name: 'SizeGuide',

  components: {
    ResponsiveImage,
    SizeGuideTable,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: () => ({}),
    },

    showSizeGuide: {
      type: Boolean,
      default: false,
    },

    variant: {
      type: [Boolean, Object],
      default: () => ({}),
    },
  },

  emits: {
    'size-guide-action': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      variantColumn: null,
      tables: {
        sizeGuideTable: {
          data: null,
          fields: null,
        },
        bodyMeasurementTable: {
          data: null,
          fields: null,
        },
      },
      scrollElement: false,
      showInches: this.showInchesAsDefault(),
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Computes if there is content to be displayed in the Size guide image
     * modal footer.
     * @returns {Boolean}
     */
    displaySizeGuideImageFooter() {
      if (
        !this.product?.sizeGuide?.keyPoints?.length &&
        !this.product?.sizeGuide?.modelDescription
      ) {
        return false
      }

      return true
    },

    /**
     * Computes whether to display the size guide image modal.
     * @returns {Boolean}
     */
    displaySizeGuideImages() {
      if (this.isMobile) {
        return false
      }

      if (!this.showSizeGuide) {
        return false
      }

      if (
        !this.product?.sizeGuide?.sizeDiagram &&
        !this.product?.sizeGuide?.sizeDiagramDetail
      ) {
        return false
      }

      return true
    },

    /**
     * Returns the appropriate size guide image to display.
     * @returns {String}
     */
    sizeGuideImage() {
      return this.product?.sizeGuide?.sizeDiagramDetail ??
        this.product?.sizeGuide?.sizeDiagram
    },

    /**
     * Helper function to compute when to display the switcher
     * for the conversion table.
     */
    showSwitcher() {
      return this.product?.type === 'Denim'
    },

    /**
     * Returns the body measurement data that corresponds to the selected size.
     * @returns {Array}.
     */
    bodyMeasurementData() {
      /* eslint-disable array-callback-return */
      const rawData = this.tables?.bodyMeasurementTable?.data?.find((size) => {
        if (Object.values(size).includes(this.variant?.title)) {
          return size
        }
        return null
      })
      /* eslint-enable array-callback-return */

      if (!rawData) {
        return null
      }

      const data = []
      for (const [key, value] of Object.entries(rawData)) {
        if (value.toUpperCase() !== this.variant?.title) {
          data.push({
            name: key,
            value,
          })
        }
      }

      return data
    },
  },

  watch: {

    /**
     * When the images are displayed, set a bodyScrollLock to prevent the page
     * being scrolled behind the image overlay.
     */
    displaySizeGuideImages(value) {
      if (value) {
        this.getScrollElement()
        disableBodyScroll(this.scrollElement)
        return
      }

      if (this.scrollElement) {
        enableBodyScroll(this.scrollElement)
      } else {
        clearAllBodyScrollLocks()
      }
    },

    /**
     * When the product updates, build new size guide tables.
     */
    product: {
      immediate: true,
      handler(product) {
        if (!product) {
          return
        }
        this.createTables()
      },
    },

    /**
     * Update the selected column when the variant changes.
     */
    variant: {
      immediate: true,
      handler(variant) {
        if (!variant) {
          this.variantColumn = null
          return
        }

        this.getSizeFromVariant()
      },
    },

    /**
     * Update the selected column when the table updates also, this is
     * required as on first-load the table is not always available at the
     * time that the variant is first updated.
     */
    'tables.sizeGuideTable.fields': {
      deep: true,
      handler() {
        this.getSizeFromVariant()
      },
    },
  },

  methods: {

    /**
     * Used to show inches as default on US store
     * @returns {Boolean}
     */
    showInchesAsDefault() {
      return window?.cnvs?.store?.permanentDomain === '//toteme-studio.myshopify.com/'
    },

    /**
     * Converts the given size to the inches equivalent.
     * @param {Number} size - Size in cm.
     * @returns {Number}
     */
    showSizeByUnit(size) {
      return this.showInches ? parseFloat(size / 2.54).toFixed(1) : size
    },

    /**
     * Get scroll element.
     */
    getScrollElement() {
      const scrollElement = document.querySelector('.main-product__grid')

      if (!scrollElement) {
        this.scrollElement = false
        return
      }

      this.scrollElement = scrollElement
    },

    /**
     * When the column variant updates, emit an event to update the related
     * variant product.
     * @param {Number} index - Index of the selected variant.
     */
    handleColumnVariantUpdate(index) {
      const title = this.tables?.sizeGuideTable?.fields?.[index]

      if (!title) {
        return
      }
      const sizeMapping = [
        { number: 32, letter: 'XXS' },
        { number: 34, letter: 'XS' },
        { number: 36, letter: 'S' },
        { number: 38, letter: 'M' },
        { number: 40, letter: 'L' },
        { number: 42, letter: 'XL' },
      ]

      // Function to map number size to letter size
      function mapNumberToLetter(number) {
        const match = sizeMapping.find(size => size.number === number)
        return match ? match.letter : null
      }

      const columnVariant = this.product?.variants?.find(variant => {
        if (!variant.title) {
          return false
        }

        const containsNumbers = str => (/\d/).test(str)
        const titleHasNumbers = containsNumbers(title)
        const variantTitleHasNumbers = containsNumbers(variant.title)

        // Check if both titles contain numbers
        if (titleHasNumbers && variantTitleHasNumbers) {
          return variant.title === title
        }

        const titleUpperCase = title.toUpperCase()
        const variantTitleUpperCase = variant.title.toUpperCase()
        const checkForLetters = (/[a-zA-Z]/).test(variantTitleUpperCase)

        if (checkForLetters) {
          // Direct comparison for titles with letters
          return variantTitleUpperCase === titleUpperCase
        }
        // Attempt to map numeric sizes to their letter equivalents and compare
        const variantNumber = parseInt(variant.title)
        if (!isNaN(variantNumber)) {
          const mappedLetter = mapNumberToLetter(variantNumber)
          return mappedLetter && mappedLetter.toUpperCase() === titleUpperCase
        }

        return false
      })

      if (!columnVariant) {
        return
      }

      const event = {
        action: null,
        variant: columnVariant,
      }

      if (columnVariant?.available) {
        event.action = 'variant-updated'
      } else {
        event.action = 'notify-me'
      }

      this.$emit('size-guide-action', event)
    },

    /**
     * Gets the selected size based on the current variant.
     */
    getSizeFromVariant() {
      const title = this.variant?.title?.toUpperCase()
      const titlePosition = this.tables?.sizeGuideTable?.fields?.indexOf(title)

      if (titlePosition < 0) {
        this.variantColumn = null
        return
      }

      this.variantColumn = titlePosition
    },

    /**
     * Determines whether to display a table.
     * @param {Object} table - Object with data relating to table fields & data.
     * @returns {Boolean}
     */
    displayTable(table) {
      return Boolean(table?.data?.length && table?.fields?.length)
    },

    /**
     * Fetches and parses the CSV data.
     */
    async createTables() {
      const sizeGuideUrl = this.product.sizeGuide?.sizeConversion
      const bodyMeasurementUrl = this.product.sizeGuide?.bodyMeasurement

      try {
        if (sizeGuideUrl) {
          const sizeGuide = await this.downloadAndParseData(sizeGuideUrl)
          this.buildSizeGuide(sizeGuide, 'sizeGuideTable')
        }

        if (bodyMeasurementUrl) {
          const bodyMeasurement = await this.downloadAndParseData(bodyMeasurementUrl)
          this.buildSizeGuide(bodyMeasurement, 'bodyMeasurementTable')
        }
      } catch (error) {
        throw new Error(error)
      }
    },

    /**
     * Handles the parsing of the data.
     * @param {String} url - URL where the CSV document can be downloaded.
     * @returns {Object}
     */
    downloadAndParseData(url) {
      return new Promise((resolve, reject) => {
        Papa.parse(url, {
          download: true,
          header: true,
          slipEmptyLines: true,
          complete: (results) => {
            resolve(results)
          },
          error: (error) => {
            reject(error)
          },
        })
      })
    },

    /**
     * Adds parsed csv results to the applicable object in component data.
     * @param {Object} result - Parsed CSV data.
     * @param {String} table - Name of the object key to store the data into.
     */
    buildSizeGuide(result, table) {
      this.tables[table].data = result?.data
      this.tables[table].fields = result?.meta?.fields
    },
  },
}
</script>

<style lang="scss">
@import './size-guide';
</style>
